import React, { useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useForm, FormProvider, useWatch, useFormState } from 'react-hook-form';
import { Grid } from '@material-ui/core';
import RMSCommonDialog from '../../../shared/components/dialog/RMSCommonDialog';
import { RMSConfirmationDialog } from '../../../shared/components/dialog';
import {
  RMSTextField,
  RMSSelect,
  RMSCheckbox,
  RMSDatePicker,
} from '../../../shared/components/form-controls';
import RMSRecordHistoryButton from '../../../shared/components/modules/RMSRecordHistoryButton';
import SearchPlaceAutoComplete from '../../../shared/components/modules/SearchPlaceAutoComplete';
import SSNTextMask from 'components/RMSTextMask/SSNTextMask';
import AddPlaceDialog from './AddPlaceDialog';
import CreateFormResolver from '../../../shared/utility/createFormResolver';
import { executeApiAction } from '../../../shared/utility/apiHelper';
import { notify } from 'reducers/NotifierReducer';
import useConfirmDialog from '../../../shared/utility/useConfirmDialog';
import personCredentialSchema from '../../schema/personCredentialSchema';
import {
  DEFAULT_VALUES,
  DICT_SELECT_PROPS,
  CREDENTIAL_TYPE,
} from '../../constants/personCredentialFormConstants';
import useUserDictionaryOptions from '../../hooks/useUserDictionaryOptions';
import { createPersonCredential, editPersonCredential } from '../../services/userService';

const PersonCredentialDialog = ({ open, onClose, personId, credential = null, onSaved }) => {
  const dispatch = useDispatch();
  const currentUser = useSelector((state) => state.user.userData.user);
  const [submitting, setSubmitting] = useState(false);
  const [addPlaceDialogOpen, setAddPlaceDialogOpen] = useState(false);
  const isEditMode = Boolean(credential?.ptsCredentialID);

  const methods = useForm({
    resolver: CreateFormResolver(personCredentialSchema),
    mode: 'onSubmit',
    defaultValues: { ...DEFAULT_VALUES, ...(credential || {}) },
  });

  const { isDirty } = useFormState({ control: methods.control });
  const { confirmState, requestConfirm, handleYes, handleNo } = useConfirmDialog();
  const {
    credentialTypeOptions,
    credentialClassOptions,
    stateOptions,
  } = useUserDictionaryOptions();
  const credentialType = useWatch({ control: methods.control, name: 'Type' });
  const ptsIssuedByID = useWatch({ control: methods.control, name: 'ptsIssuedByID' });

  const handlePlaceCreated = useCallback(
    (ptsPlaceID) => {
      methods.setValue('ptsIssuedByID', ptsPlaceID ?? null, {
        shouldDirty: true,
        shouldValidate: true,
      });
      setAddPlaceDialogOpen(false);
    },
    [methods]
  );

  const onSubmit = useCallback(
    async (data) => {
      const payload = {
        Type: data.Type || null,
        Number: data.Number,
        State: data.State || null,
        Class: data.Class || null,
        IsCommercial: data.IsCommercial || false,
        ptsIssuedByID: data.ptsIssuedByID || null,
        Issued: data.Issued || null,
        Expiration: data.Expiration || null,
        Description: data.Description || null,
        Notes: data.Notes || null,
        UpdatedBy: currentUser?.Username,
      };

      if (!isEditMode) {
        payload.ptsPersonID = personId;
        payload.CreatedBy = currentUser?.Username;
      }

      await executeApiAction({
        setLoading: setSubmitting,
        dispatch,
        action: () =>
          isEditMode
            ? editPersonCredential(credential.ptsCredentialID, payload)
            : createPersonCredential(payload),
        onSuccess: (savedCredential) => {
          dispatch(
            notify(
              isEditMode ? 'Credential updated successfully' : 'Credential created successfully',
              'success'
            )
          );
          onSaved?.(savedCredential);
        },
      });
    },
    [isEditMode, credential, personId, currentUser, dispatch, onSaved]
  );

  const handleSave = methods.handleSubmit(onSubmit);

  const requestClose = useCallback(() => {
    if (isDirty) {
      requestConfirm(
        'You have unsaved changes on this credential. Are you sure you want to close without saving?',
        () => onClose?.(),
        'Unsaved Changes'
      );
      return;
    }

    onClose?.();
  }, [isDirty, requestConfirm, onClose]);

  return (
    <RMSCommonDialog
      open={open}
      title={isEditMode ? 'Edit Credential' : 'Add Credential'}
      onClose={requestClose}
      onSave={handleSave}
      loading={submitting}
      saveDisabled={submitting}
      maxWidth="sm"
      fullWidth
      closeOnBackdropClick={false}
      saveButton={{
        show: true,
        type: 'simple',
        label: 'Save',
        variant: 'contained',
        color: 'primary',
      }}
      cancelButton={{
        show: true,
        type: 'simple',
        label: 'Close',
        variant: 'outlined',
        color: 'secondary',
      }}
      leftActions={
        isEditMode && (
          <RMSRecordHistoryButton
            disabled={!credential?.Created}
            created={credential?.Created}
            createdBy={credential?.CreatedBy}
            updated={credential?.Updated}
            updatedBy={credential?.UpdatedBy}
          />
        )
      }>
      <FormProvider {...methods}>
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <RMSSelect
              name="Type"
              label="Type"
              options={credentialTypeOptions}
              {...DICT_SELECT_PROPS}
            />
          </Grid>
          <Grid item xs={6}>
            <RMSTextField
              name="Number"
              label="Number"
              disabled={!credentialType}
              inputProps={{ maxLength: 50 }}
              InputProps={
                credentialType === CREDENTIAL_TYPE.SSN ? { inputComponent: SSNTextMask } : undefined
              }
            />
          </Grid>
          <Grid item xs={6}>
            <RMSSelect name="State" label="State" options={stateOptions} {...DICT_SELECT_PROPS} />
          </Grid>
          <Grid item xs={6}>
            <RMSSelect
              name="Class"
              label="Class"
              options={credentialClassOptions}
              {...DICT_SELECT_PROPS}
            />
          </Grid>
          <Grid item xs={6}>
            <RMSCheckbox name="IsCommercial" label="Commercial" />
          </Grid>
          <Grid item xs={6}>
            <SearchPlaceAutoComplete
              name="ptsIssuedByID"
              label="Issued By"
              defaultPlaceId={ptsIssuedByID}
              onAddIconClick={() => setAddPlaceDialogOpen(true)}
              showDetailsIcon={false}
            />
          </Grid>
          <Grid item xs={6}>
            <RMSDatePicker name="Issued" label="Issued Date" />
          </Grid>
          <Grid item xs={6}>
            <RMSDatePicker name="Expiration" label="Expiration Date" />
          </Grid>
          <Grid item xs={12}>
            <RMSTextField name="Description" label="Description" inputProps={{ maxLength: 2000 }} />
          </Grid>
          <Grid item xs={12}>
            <RMSTextField
              name="Notes"
              label="Notes"
              multiline
              rows={3}
              inputProps={{ maxLength: 2000 }}
            />
          </Grid>
        </Grid>
      </FormProvider>

      {addPlaceDialogOpen && (
        <AddPlaceDialog
          open={addPlaceDialogOpen}
          onClose={() => setAddPlaceDialogOpen(false)}
          onPlaceCreated={handlePlaceCreated}
        />
      )}

      {confirmState.open && (
        <RMSConfirmationDialog
          open={confirmState.open}
          title={confirmState.title}
          message={confirmState.message}
          onNo={handleNo}
          onYes={handleYes}
        />
      )}
    </RMSCommonDialog>
  );
};

export default PersonCredentialDialog;
